import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Flex, Box } from 'reflexbox';
import { ApiResps, apiCall } from '~/providers';
import { useWebSocketLazy } from '~/helpers/wsConnection';
import { useStateVar } from '~/helpers/useStateVar';
import { toast } from 'react-toastify';
import {
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Line, LineChart,
} from 'recharts';
import { colors } from '~/styles/colors';
import {
  GraphContainer, TitleFieldNobreak, TitleColumn,
  LoaderModal,
  StatusRealTime,
  HeaderDevice,
  HeaderDeviceInfo,
  ErrorPageContainer,
  ButtonWithoutStyle,
} from './styles';
import { useTranslation } from 'react-i18next';
import { RefreshIcon } from '~/icons';
import moment from 'moment';
import { StyledLink } from '../styles';
import { Button, Loader } from '~/components';
import {
  acessorUtilities, getAnalogicPort, treatGenerator, treatNobreak, treatTelemetryElevatorAndEscalator,
} from '../AcessorUtilities';
import { AssetStatus } from '~/components/AssetStatus';
import disconnectedIcon from '../../../../assets/img/cool_ico/Disconnected.svg';
import { ErrorImg } from '~/pages/RemoteControl/MachinesList/style';
import wifiOffline from '../../../../assets/img/cool_ico/WifiOffline.svg';

export const DmtRealTime = (props: {dmtId: string}): JSX.Element => {
  const { dmtId } = props;
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('tituloPagDielEnergiaTempoReal')}</title>
      </Helmet>
      <DmtRealTimeContent dmtId={dmtId} />
    </>
  );
};

export function DmtRealTimeContent({ dmtId }: { dmtId: string }): JSX.Element {
  const { t } = useTranslation();
  const [state, render, setState] = useStateVar({
    telemetries: [] as {pulses: number| null, status: string}[], // { status }[]
    graphData: [] as {x: string, value: number | null }[],
    lastConsumption: 0 as number,
    lastConsumptionCube: 0 as number,
    timezoneOffset: 0,
    electricalNetwork: {} as { port: string, id: number },
    storeLoad: {} as { port: string, id: number },
    utilitiesList: [] as { type?: string, port: string, port2?: number, id: number, name?: string, datId?: string}[],
    F1: [] as { x: string, value: number | null }[],
    F2: [] as { x: string, value: number | null }[],
    F3: [] as { x: string, value: number | null }[],
    F4: [] as { x: string, value: number | null }[],
    P0: [] as { x: string, value: number | null }[],
    P1: [] as { x: string, value: number | null }[],
    typeUtilitiesAccessor: {} as Record<string, { type?: string, port2?: number }>,
    portsInfo: {} as ApiResps['/dmt/get-dmt-ports-info'],
    initialTimeRef: new Date().toISOString().substring(0, 19),
    onTelmTimer: undefined as undefined|NodeJS.Timeout,
    hasNewTelm: false,
    error: false,
    status: '' as string,
    isLoading: true,
  });
  useEffect(() => {
    (async () => {
      try {
        if (dmtId) {
          await apiCall('/get-timezone-offset-by-devId', { devId: dmtId }).then((tzOffset) => {
            if (tzOffset != null) state.timezoneOffset = tzOffset;
          });
          await apiCall('/dmt/get-dmt-ports-info', { DMT_CODE: dmtId }).then((portInfo) => state.portsInfo = portInfo);
          if (state.portsInfo) {
            getPortsInfoGraph();
          }
          wsl.start(onWsOpen, onWsMessage, beforeWsClose);
          render();
        }
      } catch (err) {
        toast.error(t('erroDadosDispositivo'));
        console.log(err);
        setState({ error: true });
      }
    })();
  }, []);

  function getPortsInfoGraph() {
    const listUtilities: { type?: string, port: string, port2?: number, id: number, name?: string, datId?: string }[] = [];

    for (const port of state.portsInfo.portsDigitals) {
      if (port.associated && port.utilityId) {
        listUtilities.push({
          port: `F${port.port}`,
          id: port.utilityId,
          name: port.name,
          datId: port.datId,
          type: port.type,
          port2: port.port2,
        });
        if (port.port2) {
          state.storeLoad = {
            port: `F${port.port2}`,
            id: port.utilityId,
          };
        }
      }
      if (port.associated && port.illuminationId) {
        listUtilities.push({
          port: `F${port.port}`,
          id: port.illuminationId,
          name: port.name,
        });
      }
      if (port.associated && port.eletricCircuitId) {
        state.electricalNetwork = {
          port: `F${port.port}`,
          id: port.eletricCircuitId,
        };
      }
    }

    for (const port of state.portsInfo.portsAnalogics) {
      if (port.associated && port.utilityId) {
        listUtilities.push({
          port: `P${port.port - 1}`,
          id: port.utilityId,
          name: port.name,
          datId: port.datId,
          type: port.type,
        });
      }
    }
    if (state.storeLoad.port) {
      state.utilitiesList = listUtilities.filter((item) => item.port !== state.storeLoad.port);
    } else {
      state.utilitiesList = listUtilities;
    }
    const typeUtilitiesAccessor = listUtilities.reduce((acc, item) => {
      if (!acc[item.port]) {
        acc[item.port] = {};
      }
      acc[item.port].type = item.type;
      acc[item.port].port2 = item.port2;
      return acc;
    }, {} as Record<string, { type?: string, port2?: number }>);
    state.typeUtilitiesAccessor = typeUtilitiesAccessor;
  }

  function getTelemetryValue(port: string, telemetry) {
    const typeUtility = state.typeUtilitiesAccessor[port]?.type;
    const port2 = state.typeUtilitiesAccessor[port]?.port2;
    if (typeUtility === 'Elevator' || typeUtility === 'Escalator') {
      const status = treatTelemetryElevatorAndEscalator(
        getAnalogicPort(port, telemetry), telemetry[state.electricalNetwork.port],
      );
      return status;
    }
    if (typeUtility === 'Nobreak') {
      const status = treatNobreak(
        telemetry[port],
        telemetry[state.electricalNetwork.port],
      );
      return status;
    }
    if (typeUtility === 'Generator' && port !== `F${port2}`) {
      const status = treatGenerator(
        telemetry[port],
        telemetry[`F${port2}`],
        telemetry[state.electricalNetwork.port],
      );
      return status;
    }
    if (telemetry[port]) return 1;
    return 0;
  }

  function processReceivedTelemetry(telemetry) {
    state.status = telemetry.status || '';
    state.F1.push({ x: telemetry.timestamp, value: getTelemetryValue('F1', telemetry) });
    state.F2.push({ x: telemetry.timestamp, value: getTelemetryValue('F2', telemetry) });
    state.F3.push({ x: telemetry.timestamp, value: getTelemetryValue('F3', telemetry) });
    state.F4.push({ x: telemetry.timestamp, value: getTelemetryValue('F4', telemetry) });
    state.P0.push({ x: telemetry.timestamp, value: getTelemetryValue('P0', telemetry) });
    state.P1.push({ x: telemetry.timestamp, value: getTelemetryValue('P1', telemetry) });
    state.F1.length > 6 && state.F1.shift();
    state.F2.length > 6 && state.F2.shift();
    state.F3.length > 6 && state.F3.shift();
    state.F4.length > 6 && state.F4.shift();
    state.P0.length > 6 && state.P0.shift();
    state.P1.length > 6 && state.P1.shift();
  }

  const wsl = useWebSocketLazy();

  function onWsOpen(wsConn) {
    wsConn.send({ type: 'dmtSubscribeRealTime', data: { DMT_CODE: dmtId } });
  }

  function onWsMessage(response) {
    if (response && response.type === 'dmtTelemetry') {
      const data = moment(new Date(response.data.timestamp || response.data.deviceTimestamp));
      response.data.timestamp = data.format('YYYY-MM-DDTHH:mm:ss').split('T')[1];
      processReceivedTelemetry(response.data);
      render();
    }
  }

  function beforeWsClose(wsConn) {
    wsConn.send({ type: 'dmtUnsubscribeRealTime' });
  }

  if (state[state.electricalNetwork.port]) {
    state[state.electricalNetwork.port].forEach((data) => {
      if (data.value > 1) data.value = 1;
    });
  }

  if (state.error) {
    return <PageError dmtId={dmtId} t={t} />;
  }

  if (state.status === 'OFFLINE') {
    return <PageOffline dmtId={dmtId} t={t} />;
  }

  return (
    <Flex>
      <Box width={1}>
        <Flex justifyContent="left" alignItems="left" flexDirection="column" flexWrap="wrap" width="100%">
          <HeaderDevice>
            <HeaderDeviceInfo>
              <h3>{t('dispositivo')}</h3>
              <span>{dmtId}</span>
            </HeaderDeviceInfo>
            <AssetStatus
              key={dmtId}
              DUT_ID={null}
              DEV_AUT={dmtId}
              DEV_ID={dmtId}
              isAutomation
              withoutMarginTop
            />
          </HeaderDevice>
          {
            state[state.storeLoad.port] && (
              <Box width={[1, 1, 1, 1, 1]} paddingBottom={35}>
                <TitleFieldNobreak>
                  <TitleColumn
                    style={{
                      display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%',
                    }}
                  >
                    <h3>
                      <strong>{t('cargaLoja')}</strong>
                    </h3>
                    <span>
                      {t('feedback')}
                      :
                      <strong>{` ${state.storeLoad.port}`}</strong>
                    </span>
                  </TitleColumn>
                </TitleFieldNobreak>
                <GraphContainerAll type="Electric Network" data={state[state.storeLoad.port]} ticks={acessorUtilities['Electric Network'].ticks} domain={acessorUtilities['Electric Network'].domain} t={t} />
              </Box>
            )
          }
          {
            state[state.electricalNetwork.port] && (
              <Box width={[1, 1, 1, 1, 1]} paddingBottom={35}>
                <TitleFieldNobreak>
                  <TitleColumn>
                    <h3
                      style={{
                        fontWeight: 'bold', margin: 0, color: colors.Blue300, fontSize: 18,
                      }}
                    >
                      {t('monitoramentoRedeEletrica')}
                    </h3>
                    <h3>
                      <strong>{t('statusRedeEletrica')}</strong>
                    </h3>
                  </TitleColumn>
                </TitleFieldNobreak>
                <GraphContainerAll type="Electric Network" data={state[state.electricalNetwork.port]} ticks={acessorUtilities['Electric Network'].ticks} domain={acessorUtilities['Electric Network'].domain} t={t} />
              </Box>
            )
          }
          {
            state.utilitiesList.length > 0 && (
              <TitleFieldNobreak>
                <TitleColumn>
                  <h3
                    style={{
                      fontWeight: 'bold', margin: 0, color: colors.Blue300, fontSize: 18,
                    }}
                  >
                    {t('statusUtilitarios')}
                  </h3>
                </TitleColumn>
              </TitleFieldNobreak>
            )
          }
          { state.utilitiesList.length > 0 && state.utilitiesList.map(((nobreak) => (
            <UtilityGraph port={nobreak.port} type={nobreak.type} name={nobreak.name} datId={nobreak.datId} data={state[nobreak.port]} t={t} />
          ))) }
        </Flex>
      </Box>
    </Flex>
  );
}

const NobreakCustomTick = (props) => {
  const {
    x, y, payload, anchor, type,
  } = props;
  const labelRaw = acessorUtilities[type]?.states[payload.value] || 'teste';

  return (
    <g transform={`translate(${x - 3},${anchor ? y : y - 12})`}>
      <text x={0} y={0} dy={16} textAnchor={anchor || 'end'} fill="#666" fontSize="10px" style={{ maxWidth: 14, textTransform: 'capitalize' }}>
        {labelRaw.map((line, index) => (
          <tspan key={line} x={0} dy={index === 0 ? 16 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

function UtilityGraph({
  name, datId, data, t, type, port,
}: Readonly<{ name?: string, datId?: string, type?: string, data, t, port: string }>) {
  const lastValue = data && data[data.length - 1]?.value;
  if (!type || !acessorUtilities[type]) return <></>;
  return (
    <Box paddingBottom={35} width={[1, 1, 1, 1, 1]}>
      <TitleFieldNobreak style={{ flexWrap: 'wrap' }}>
        <div style={{ display: 'flex' }}>
          <TitleColumn>
            <div style={{ flexWrap: 'wrap' }}>
              <h3
                style={{
                  fontWeight: 'bold', margin: 0, display: 'flex', gap: 5,
                }}
              >
                {acessorUtilities[type]?.icon}
                { name || '-' }
              </h3>
              {(lastValue !== null) && (
                <StatusRealTime>
                  {acessorUtilities[type]?.states[lastValue]?.join(' ')}
                </StatusRealTime>
              )}
            </div>
            <h4>
              <StyledLink to={`/analise/ativo/${datId}/informacoes`}>
                {datId || ''}
              </StyledLink>
            </h4>
          </TitleColumn>
        </div>
        <div>
          {
            type !== 'Generator' && (
              <span>
                {acessorUtilities[type]?.selectName}
                :
                <strong>
                  {` ${port}`}
                </strong>
              </span>
            )
          }
        </div>
      </TitleFieldNobreak>
      <GraphContainerAll type={type} data={data} ticks={acessorUtilities[type]?.ticks} domain={acessorUtilities[type]?.domain} t={t} />
    </Box>
  );
}

const CustomXAxisTick = (props) => {
  const {
    x, y, payload, index, anchor, type,
  } = props;
  const isFirst = index === 0;
  const textAnchor = isFirst ? 'start' : 'middle';

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={8}
        textAnchor={textAnchor}
        fontSize="10px"
      >
        {payload.value}
      </text>
    </g>
  );
};

function GraphContainerAll({
  data, type, domain, ticks, t,
}) {
  if (data.length === 0) {
    return (
      <Loader />
    );
  }
  return (
    <GraphContainer style={{ position: 'relative' }}>
      {
        (data && data.length && data.length < 2) && (
          <LoaderModal>
            <Loader />
            <p>{t('carregandoDados')}</p>
          </LoaderModal>
        )
      }
      { (data && data.length > 0) && (
      <ResponsiveContainer key={`rc_${data?.length}`} width="100%" height={150}>
        <LineChart
          width={600}
          height={500}
          data={data}
          margin={{
            top: 5, right: 20, bottom: 5, left: 35,
          }}
        >
          <Line type="monotone" dataKey="value" stroke={colors.BlueSecondary} strokeWidth={2} dot={false} isAnimationActive={false} />
          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis dataKey="x" type="category" tick={<CustomXAxisTick />} />
          <YAxis
            dataKey="value"
            type="number"
            tick={<NobreakCustomTick type={type} t={t} />}
            ticks={ticks}
            interval={0}
            domain={domain}
          />
        </LineChart>
      </ResponsiveContainer>
      ) }

    </GraphContainer>
  );
}

function PageError({ dmtId, t }) {
  return (
    <div>
      <HeaderDevice>
        <HeaderDeviceInfo>
          <h3>{t('dispositivo')}</h3>
          <span>{dmtId}</span>
        </HeaderDeviceInfo>
        <AssetStatus
          key={dmtId}
          DUT_ID={null}
          DEV_AUT={dmtId}
          DEV_ID={dmtId}
          isAutomation
          withoutMarginTop
        />
      </HeaderDevice>
      <ErrorPageContainer>
        <ErrorImg src={disconnectedIcon} alt="img-disconnected" />
        <p>{t('erroCarregarTempoRealDmt')}</p>
        <Button
          variant="primary"
          style={{
            textTransform: 'unset', maxWidth: 150, borderRadius: 5, fontSize: 11,
          }}
        >
          <ButtonWithoutStyle style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => window.location.reload()}>
            {t('recarregarPagina')}
            <RefreshIcon />
          </ButtonWithoutStyle>
        </Button>
      </ErrorPageContainer>
    </div>
  );
}

function PageOffline({ dmtId, t }) {
  return (
    <div>
      <HeaderDevice>
        <HeaderDeviceInfo>
          <h3>{t('dispositivo')}</h3>
          <span>{dmtId}</span>
        </HeaderDeviceInfo>
        <AssetStatus
          key={dmtId}
          DUT_ID={null}
          DEV_AUT={dmtId}
          DEV_ID={dmtId}
          isAutomation
          withoutMarginTop
        />
      </HeaderDevice>
      <ErrorPageContainer style={{ gap: 10, padding: '20px 30%' }}>
        <ErrorImg src={wifiOffline} alt="img-disconnected" />
        <strong>{t('dispositivoOffilne')}</strong>
        <p>{t('naoEPossivelVisualizarTempoReal')}</p>
      </ErrorPageContainer>
    </div>
  );
}
